import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { CardWithContent } from 'src/components/molecules/card-with-content';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { CompactPagination } from 'src/components/organisms/compact-pagination';
import { fetchBookings } from 'src/features/bookings/api/lib/bookings';
import { bookingsQueryKeys } from 'src/features/bookings/api/query-keys/bookings';
import { useAssignEmployeesToEmployeeGroup } from 'src/features/employees-groups/api/hooks/employees/use-assign-employees-to-employee-group';
import { fetchEmployeesGroups } from 'src/features/employees-groups/api/lib/employees-groups';
import { employeesGroupsKeys } from 'src/features/employees-groups/api/query-keys/employees-groups';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { useDialog } from 'src/hooks/use-dialog';
import { usePagination } from 'src/hooks/use-pagination';
import { useSearchState } from 'src/hooks/use-search-state';
import { useUserStore } from 'src/store/user-store';
import { EmployeeHasActiveOrFutureBookingsDialog } from './employee-has-active-or-future-bookings-dialog';

interface Props extends DialogActions {
  selectedEmployeeToken: string;
  selectedEmployeeGroupToken: string;
}

export const ChangeEmployeeGroupDialog = ({
  open,
  onCancel,
  selectedEmployeeToken,
  selectedEmployeeGroupToken,
}: Props) => {
  const { t } = useTranslation(['employees']);

  const [selectedEmployeeGroup, setSelectedEmployeeGroup] =
    useState<string>('');

  const {
    page,
    perPage,
    changePage,
    changePerPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const { directoryToken } = useUserStore();

  const [searchState, setSearchState] = useSearchState();

  const { results, isLoading, isFetching, error } = useMeilisearch({
    fetch: fetchEmployeesGroups,
    page,
    perPage,
    queryKey: employeesGroupsKeys.list(
      directoryToken ?? '',
      perPage,
      page,
      searchState,
    ),
    searchState,
  });

  useEffect(() => {
    if (!results) return;
    setTotalItems(results.estimatedTotalHits);
  }, [results]);

  const handleOnCancel = () => {
    onCancel?.();
    setSelectedEmployeeGroup('');
  };

  const {
    mutate: assignEmployeesToEmployeeGroup,
    isPending: isAssigningEmployeesToEmployeeGroup,
  } = useAssignEmployeesToEmployeeGroup(handleOnCancel);

  const handleChangeQuery = (query: string) => {
    setSearchState(prev => ({ ...prev, q: query }));
  };

  const { results: bookings } = useMeilisearch({
    fetch: fetchBookings,
    queryKey: bookingsQueryKeys.list(directoryToken ?? '', 1, 1000),
    page: 1,
    perPage: 1000,
    searchState: {
      filter: [
        [`employeeBookerToken = ${selectedEmployeeToken}`],
        ['status = "BOOKED"'],
      ],
      sort: [],
      q: '',
    },
    enabled: Boolean(selectedEmployeeToken),
  });

  // Employee has active or future bookings
  const [
    isOpenEmployeeHasActiveOrFutureBookingsDialog,
    handleOpenEmployeeHasActiveOrFutureBookingsDialog,
    handleCloseEmployeeHasActiveOrFutureBookingsDialog,
  ] = useDialog();

  const handleChangeEmployeeGroup = () => {
    if (bookings?.hits && bookings.hits.length > 0) {
      handleOpenEmployeeHasActiveOrFutureBookingsDialog();
      return;
    }

    assignEmployeesToEmployeeGroup({
      employeeGroupToken: selectedEmployeeGroup,
      employeesTokens: [selectedEmployeeToken],
      bookingsDecisions: {
        cancelBookingDecisions: [],
        keepBookingUnchangedDecisions: [],
        moveBookingDecisions: [],
      },
      bookingsDecisionsReason: t('employees:employees.bookingsDecisionsReason'),
    });
  };

  const isEmployeeGroupDisabled = (groupToken: string) =>
    selectedEmployeeGroupToken === groupToken;

  const isEmployeeGroupSelected = (groupToken: string) =>
    selectedEmployeeGroup === groupToken;

  return (
    <>
      <Dialog
        title={t('employees:employees.changeEmployeeGroupDialog.title')}
        open={open}
        onCancel={handleOnCancel}
        onConfirm={handleChangeEmployeeGroup}
        isConfirmButtonDisabled={selectedEmployeeGroup === ''}
        isConfirmButtonSubmitting={isAssigningEmployeesToEmployeeGroup}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">
            {t('employees.changeEmployeeGroupDialog.message')}
          </Typography>

          <Query
            searchAsType
            disabled={false}
            onChange={(newQuery: string) => {
              handleChangeQuery(newQuery);
            }}
            sx={{
              minWidth: 200,
            }}
            value={searchState.q}
          />

          <DataStatusIndicator
            dataLength={results?.hits?.length ?? 0}
            isFetching={isLoading}
            error={error?.response?.data?.message}
          />

          <Box>
            {results?.hits?.map(group => (
              <CardWithContent
                key={group.token}
                onClick={() => setSelectedEmployeeGroup(group.token)}
                isSelected={isEmployeeGroupSelected(group.token)}
                invalid={isEmployeeGroupDisabled(group.token)}>
                <Typography variant="body2">{group.name}</Typography>
              </CardWithContent>
            ))}
          </Box>

          <CompactPagination
            disabled={isLoading}
            isFetching={isFetching}
            page={page}
            changePage={changePage}
            handlePerPageChange={changePerPage}
            nextDisabled={nextDisabled}
            nextPage={nextPage}
            prevDisabled={prevDisabled}
            prevPage={prevPage}
            perPage={perPage}
            totalPages={totalPages}
          />
        </Box>
      </Dialog>

      <EmployeeHasActiveOrFutureBookingsDialog
        open={isOpenEmployeeHasActiveOrFutureBookingsDialog}
        onCancel={() => {
          handleCloseEmployeeHasActiveOrFutureBookingsDialog();
          handleOnCancel();
        }}
        employeeGroupToken={selectedEmployeeGroup}
        employeeToken={selectedEmployeeToken}
        bookingsToManage={bookings?.hits ?? []}
      />
    </>
  );
};
